import { CommanderActionDef } from "./redemptionActions";

/**
 * Alerts module actions (navbar bell, inbox page, detail page).
 * `action_id` keys are stable; `name` / `description` are what GA users read.
 * Event names are built in `alertsTracking.ts` as `alerts_{surface}_{action}`.
 */
export const ALERTS_ACTIONS = {
  // Bell (navbar)
  bell_open: {
    name: "Open Alerts Bell",
    description: "User opened the alerts panel from the navbar bell",
  },
  view_all: {
    name: "Open Alerts Inbox",
    description: "User clicked View all in the bell panel to open the inbox",
  },

  // Filters (bell + inbox)
  view_change: {
    name: "Change Alerts View",
    description:
      "User switched view tab (All / Unread / Open / Resolved / Archived)",
  },
  section_change: {
    name: "Change Alerts Section",
    description: "User filtered alerts by section (or All sections)",
  },
  severity_change: {
    name: "Change Severity Filter",
    description:
      "User filtered the inbox by severity (High / Medium / Low / All)",
  },
  stat_tile_click: {
    name: "Click Alerts Stat Tile",
    description:
      "User clicked an inbox stat tile (Unread / High priority / Open)",
  },
  page_change: {
    name: "Change Alerts Page",
    description: "User moved to another page of the alerts inbox",
  },
  refresh: {
    name: "Refresh Alerts",
    description: "User clicked Refresh on the alerts inbox",
  },
  retry: {
    name: "Retry Loading Alerts",
    description: "User clicked Try again after alerts failed to load",
  },

  // Bulk
  mark_all_read: {
    name: "Mark All Read",
    description: "User marked all alerts read, globally or for one section",
  },
  select_all: {
    name: "Select All Alerts",
    description: "User toggled select-all for the current inbox page",
  },
  bulk_read: {
    name: "Bulk Mark Read",
    description: "User marked the selected alerts as read",
  },
  bulk_archive: {
    name: "Bulk Archive",
    description: "User archived the selected alerts",
  },
  bulk_unarchive: {
    name: "Bulk Unarchive",
    description: "User moved the selected archived alerts back to the inbox",
  },
  bulk_clear: {
    name: "Clear Selection",
    description: "User cleared the alert selection",
  },

  // Row
  row_open: {
    name: "Open Alert",
    description:
      "User clicked an alert row; it was marked read and deep linked",
  },
  row_detail: {
    name: "Open Alert Detail",
    description: "User opened the alert detail page from a row action",
  },
  row_read: {
    name: "Mark Alert Read",
    description: "User marked one alert as read from the row action",
  },
  row_archive: {
    name: "Archive Alert",
    description: "User archived one alert from the row action",
  },
  row_unarchive: {
    name: "Unarchive Alert",
    description: "User moved one alert back to the inbox from the row action",
  },

  // Detail
  back: {
    name: "Back to Alerts",
    description: "User went back to the alerts inbox from an alert detail page",
  },
  open_ref: {
    name: "Open Alert Record",
    description:
      "User opened the linked record (order / project / task) from detail",
  },
  archive: {
    name: "Archive Alert",
    description: "User archived the alert from its detail page",
  },
  unarchive: {
    name: "Unarchive Alert",
    description: "User moved the alert back to the inbox from its detail page",
  },
  resolve_open: {
    name: "Open Resolve Dialog",
    description: "User clicked Resolve on an open alert",
  },
  resolve_cancel: {
    name: "Cancel Resolve",
    description: "User closed the resolve dialog without resolving",
  },
  resolve_submit: {
    name: "Resolve Alert",
    description:
      "User submitted a resolution note; outcome param has the result",
  },
} as const satisfies Record<string, CommanderActionDef>;

export type AlertsActionId = keyof typeof ALERTS_ACTIONS;
